import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router';
import { useTitle } from 'react-use';

import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import SubHeaderEntity from '@/components/organisms/SubHeader/SubHeaderEntity';
import SubNavEntity from '@/components/organisms/SubNav/SubNavEntity';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Skeleton } from '@/components/ui/skeleton';
import useBrains from '@/hooks/useBrains';
import useEntities from '@/hooks/useEntities';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectEntityName } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import styles from './Entities.module.scss';

export default function Entities() {
  const { t } = useTranslation();
  const { brainId } = useParams();
  const { brain } = useBrains(brainId);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  useTitle(t('pages.entities', { 0: brain?.name ?? '-' }));

  useSegments(PageName.ENTITIES);

  const slug = useSelector(selectAccountSlug);
  const { entities, createDraftEntity } = useEntities(brainId);
  const entityName = useSelector(selectEntityName);

  useEffect(() => {
    // If there are no entities, create a new one
    // This is the empty state for entities
    if (entities?.length === 0) {
      createDraftEntity();
    }
  }, [entities, createDraftEntity]);

  if (entities?.length) {
    const path = `/${slug}/ai-agents/${brainId}/workflows/entities/${entityName ?? entities[0].entity}`;

    return <Navigate to={path} replace />;
  }

  return (
    <>
      {!canWrite && <ReadOnlyBanner />}

      <div className={cn(styles.wrapper, { [styles.readOnly]: !canWrite })}>
        <SubNavEntity />
        <SubHeaderEntity />
        <PageContentWrapper>
          <Skeleton className="h-12 w-full" />
        </PageContentWrapper>
      </div>
    </>
  );
}
